import React, { useMemo } from "react";
import { Tooltip } from "antd";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";

import { RequestStatus } from "@fitness-app/app-store";
import { type ExerciseAggregateStats } from "@fitness-app/data-models/entities/ExerciseStats";
import { WeightHint, type ExerciseInWorkout, type ExerciseSet } from "@fitness-app/data-models/entities/TrainingProgram";
import { getWorkoutHintSettings } from "@fitness-app/utils/src/programs/getWorkoutHintSettings";
import setHintValuesForSets from "@fitness-app/utils/src/programs/setHintValuesForSets/setHintValuesForSets";

import { useProgramBuilderOptionalContext } from "~/shared/providers/ProgramBuilderProvider";
import { useAppSelector } from "~/store/initializeStore";

export interface ClientHintPreview {
  /** The hint the client actually has selected — drives the wording shown to the trainer. */
  weightHint: WeightHint;
  /** Resolves a planned set to what the client's app will actually prefill for that series. */
  getSetAsClientSeesIt: (exercise: ExerciseInWorkout, setNumber: number) => ExerciseSet | null;
}

/**
 * The trainer plans one number, but the client's app may prefill another one from their own history
 * (`weightHint` / `applyHintToRepeats`). This hook mirrors that resolution inside the admin preview
 * so the trainer can see both — the source of the TRE-1824/1825 confusion was that they could not.
 *
 * Read-only: `TraineeWorkoutDayProvider` owns the fetching, since this hook mounts per exercise row.
 * Returns null in the template builder, where there is no client to resolve against, and while the
 * store still holds another client's data.
 */
export const useClientHintPreview = (): ClientHintPreview | null => {
  const context = useProgramBuilderOptionalContext();
  const traineeId = context?.selectedProgram && "traineeId" in context.selectedProgram
    ? context.selectedProgram.traineeId
    : null;

  const traineeSettings = useAppSelector((store) => store.trainee.traineeSettings);
  const exercisesRecordsList = useAppSelector((store) => store.traineeWorkoutsStats.exercisesRecordsList);
  const exercisesRecordsStatus = useAppSelector((store) => store.traineeWorkoutsStats.exercisesRecordsListStatus);
  const exercisesRecordsTraineeId = useAppSelector((store) => store.traineeWorkoutsStats.exercisesRecordsTraineeId);

  const exercisesRecords = useMemo(
    () => keyBy(exercisesRecordsList ?? [], "exerciseId") as Record<string, ExerciseAggregateStats>,
    [exercisesRecordsList],
  );

  // Both slices are global and survive a switch between clients, so each has to be confirmed as
  // belonging to this trainee — otherwise the preview would quietly show someone else's history.
  const settingsMatchTrainee = Boolean(traineeId) && traineeSettings?.id === traineeId;
  const recordsMatchTrainee =
    Boolean(traineeId) && exercisesRecordsTraineeId === traineeId && exercisesRecordsStatus === RequestStatus.SUCCESS;
  const hintSettings = useMemo(
    () => getWorkoutHintSettings(settingsMatchTrainee ? traineeSettings?.workoutSettings : undefined),
    [settingsMatchTrainee, traineeSettings?.workoutSettings],
  );

  return useMemo(() => {
    if (!traineeId || !settingsMatchTrainee || !recordsMatchTrainee) {
      return null;
    }
    return {
      weightHint: hintSettings.weightHint,
      getSetAsClientSeesIt: (exercise, setNumber) => {
        const records = exercisesRecords[exercise.exercise.id];
        if (!records) {
          return null;
        }
        const resolved = setHintValuesForSets({
          sets: exercise.set,
          hintType: hintSettings.weightHint,
          records,
          exercise,
          applyToRepeats: hintSettings.applyHintToRepeats,
        });
        return resolved[setNumber] ?? null;
      },
    };
  }, [traineeId, settingsMatchTrainee, recordsMatchTrainee, exercisesRecords, hintSettings]);
};

/**
 * Renders what the client's app resolves for this value, underneath the planned one. A difference is
 * called out in amber; a match gets a quiet grey confirmation rather than nothing, because an empty
 * cell cannot be told apart from "the hint never applied" — which is the very ambiguity the trainer
 * is here to resolve.
 */
export const ClientSeesValue = ({
  planned,
  clientSees,
  weightHint,
  format,
}: {
  planned: ExerciseSet["repeats"] | ExerciseSet["weight"];
  clientSees: ExerciseSet["repeats"] | ExerciseSet["weight"] | undefined;
  weightHint: WeightHint | undefined;
  format: (value: ExerciseSet["repeats"] | ExerciseSet["weight"]) => React.ReactNode;
}): React.ReactElement | null => {
  const { t } = useTranslation("workouts");

  // `undefined` means the resolution never produced a set for this series (no records for the
  // exercise) — stay silent. A resolved-but-empty `null` is a real answer and handled below.
  // No hint selected likewise means there is nothing to reconcile.
  if (clientSees === undefined || !weightHint || weightHint === WeightHint.DEFAULT) {
    return null;
  }

  const hint = t(`hint.${weightHint.toLowerCase()}`);
  // The table already normalises the planned value with `?? 0`, so the resolved one has to get the
  // same treatment. Without it a set planned at 0 kg whose last workout logged no weight compares
  // 0 against null and is reported as a difference, rendered as a bare "-".
  const resolved = clientSees ?? 0;
  const matchesPlan = JSON.stringify(planned ?? 0) === JSON.stringify(resolved);

  if (matchesPlan) {
    return (
      <Tooltip title={t("hint.clientSeesMatchesPlanTooltip", { hint })}>
        <div className="text-xs text-gray-400">{t("hint.clientSeesMatchesPlan")}</div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t("hint.clientSeesFromHint", { hint })}>
      <div className="text-xs text-amber-600">
        {t("hint.clientSeesValue")}: {format(resolved)}
      </div>
    </Tooltip>
  );
};
